/**
 * Removes all whitespace-only text nodes from an element's children.
 * Useful for components like Group where whitespace between slotted
 * elements causes visual gaps.
 *
 * @param element - The parent element to clean
 */
export const removeWhitespaceNodes = (element: Element): void => {
  element.replaceChildren(
    ...[...element.childNodes].filter((node) => !(node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())),
  );
};

/**
 * Sets up automatic whitespace removal with MutationObserver.
 * Returns a cleanup function to stop observing.
 *
 * @param element - The parent element to watch
 * @returns Cleanup function to disconnect the observer
 */
export const watchAndRemoveWhitespace = (element: Element): (() => void) => {
  // Initial cleanup
  removeWhitespaceNodes(element);

  // Watch for changes
  const observer = new MutationObserver(() => {
    removeWhitespaceNodes(element);
  });

  observer.observe(element, {
    childList: true,
    characterData: true,
    subtree: false,
  });

  return () => observer.disconnect();
};
